import api from './api.service';

export type ComponentStatus = 'operational' | 'degraded' | 'partial_outage' | 'major_outage' | 'maintenance';
export type IncidentSeverity = 'none' | 'minor' | 'major' | 'critical';
export type IncidentStatus = 'investigating' | 'identified' | 'monitoring' | 'resolved';
export type MonitoredResourceType = 'host' | 'application' | 'kubernetes_cluster' | 'custom';

export interface StatusPage {
  id: string;
  tenant_id: string;
  public_url_slug: string;
  name: string;
  description?: string;
  logo_url?: string;
  custom_domain?: string;
  header_color: string;
  primary_color: string;
  show_uptime_stats: boolean;
  show_incident_history: boolean;
  uptime_display_days: number;
  timezone: string;
  language: string;
  is_enabled: boolean;
  metadata?: any;
  created_at: string;
  updated_at: string;
}

export interface StatusPageComponent {
  id: string;
  tenant_id: string;
  status_page_id: string;
  name: string;
  description?: string;
  group_name?: string;
  status: ComponentStatus;
  display_order: number;
  monitored_resource_type?: MonitoredResourceType;
  monitored_resource_id?: string;
  is_visible: boolean;
  metadata?: any;
  created_at: string;
  updated_at: string;
}

export interface StatusPageIncident {
  id: string;
  tenant_id: string;
  status_page_id: string;
  title: string;
  message?: string;
  severity: IncidentSeverity;
  status: IncidentStatus;
  started_at: string;
  resolved_at?: string;
  affected_components?: string[];
  is_visible: boolean;
  internal_incident_id?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  updates?: IncidentUpdate[];
}

export interface IncidentUpdate {
  id: string;
  tenant_id: string;
  incident_id: string;
  status: IncidentStatus;
  message: string;
  created_by?: string;
  created_at: string;
}

const statusPageApi = {
  // Status Page CRUD
  getAll: async () => {
    const response = await api.get('/v1/status-pages');
    return response;
  },

  getById: async (id: string) => {
    const response = await api.get(`/v1/status-pages/${id}`);
    return response;
  },

  create: async (data: Partial<StatusPage>) => {
    const response = await api.post('/v1/status-pages', data);
    return response;
  },

  update: async (id: string, data: Partial<StatusPage>) => {
    const response = await api.put(`/v1/status-pages/${id}`, data);
    return response;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/v1/status-pages/${id}`);
    return response;
  },

  toggle: async (id: string, enabled: boolean) => {
    const response = await api.patch(`/v1/status-pages/${id}/toggle`, { enabled });
    return response;
  },

  uploadLogo: async (id: string, file: File) => {
    const formData = new FormData();
    formData.append('logo', file);

    const response = await api.post(`/v1/status-pages/${id}/upload-logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },

  // Component CRUD
  getComponents: async (statusPageId: string) => {
    const response = await api.get(`/v1/status-pages/${statusPageId}/components`);
    return response;
  },

  createComponent: async (statusPageId: string, data: Partial<StatusPageComponent>) => {
    const response = await api.post(`/v1/status-pages/${statusPageId}/components`, data);
    return response;
  },

  updateComponent: async (statusPageId: string, id: string, data: Partial<StatusPageComponent>) => {
    const response = await api.put(`/v1/status-pages/${statusPageId}/components/${id}`, data);
    return response;
  },

  updateComponentStatus: async (statusPageId: string, id: string, status: ComponentStatus) => {
    const response = await api.patch(`/v1/status-pages/${statusPageId}/components/${id}/status`, { status });
    return response;
  },

  deleteComponent: async (statusPageId: string, id: string) => {
    const response = await api.delete(`/v1/status-pages/${statusPageId}/components/${id}`);
    return response;
  },

  reorderComponents: async (statusPageId: string, componentOrder: string[]) => {
    const response = await api.post(`/v1/status-pages/${statusPageId}/components/reorder`, {
      componentOrder,
    });
    return response;
  },

  // Incident CRUD
  getIncidents: async (statusPageId: string, params?: {
    limit?: number;
    offset?: number;
    includeResolved?: boolean;
  }) => {
    const response = await api.get(`/v1/status-pages/${statusPageId}/incidents`, { params });
    return response;
  },

  createIncident: async (statusPageId: string, data: Partial<StatusPageIncident>) => {
    const response = await api.post(`/v1/status-pages/${statusPageId}/incidents`, data);
    return response;
  },

  updateIncident: async (statusPageId: string, id: string, data: Partial<StatusPageIncident>) => {
    const response = await api.put(`/v1/status-pages/${statusPageId}/incidents/${id}`, data);
    return response;
  },

  deleteIncident: async (statusPageId: string, id: string) => {
    const response = await api.delete(`/v1/status-pages/${statusPageId}/incidents/${id}`);
    return response;
  },

  addIncidentUpdate: async (statusPageId: string, incidentId: string, data: { status: IncidentStatus; message: string }) => {
    const response = await api.post(`/v1/status-pages/${statusPageId}/incidents/${incidentId}/updates`, data);
    return response;
  },

  resolveIncident: async (statusPageId: string, incidentId: string, message: string) => {
    const response = await api.post(`/v1/status-pages/${statusPageId}/incidents/${incidentId}/resolve`, { message });
    return response;
  },

  // Public API (for preview)
  getPublicStatusPage: async (slug: string) => {
    const response = await api.get(`/v1/status/${slug}`);
    return response;
  },
};

export default statusPageApi;
